import * as React from 'react';
import { fieldValidation, rng, InternalSubscriberArticleListing, PublicAmorgUser, ArticleChangeImportance } from '../../../amorg-common';
import { createInternalArticle, getCurrentArticleOwners, getInternalArticles } from '../../../api/siteArticlesClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../designSystem/Message';
import { Modal } from '../../../designSystem/Modal';
import { PageHeader } from '../../../designSystem/PageHeader';
import { Table, TableHeaderIconWithSort, TableHeaderWithSort, TableRowWithOnClick } from '../../../designSystem/Table';
import { FullUserAvatar } from '../../../designSystem/avatar/FullUserAvatar';
import { ImportanceAvatar } from '../../../designSystem/avatar/ImportanceAvatar';
import { UserAvatar } from '../../../designSystem/avatar/UserAvatar';
import { Button } from '../../../designSystem/button/Button';
import { ToolBarContainer } from '../../../designSystem/compositeComponents/ButtonToolBar';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { UserSelectorAvatarButton } from '../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton';
import { FormField } from '../../../designSystem/formField/FormField';
import { TextField } from '../../../designSystem/formField/text/TextField';
import { Icon } from '../../../designSystem/icons/Icons';
import { HtmlEditor } from '../../../designSystem/inputs/HtmlEditor';
import { HtmlEditorViewer } from '../../../designSystem/inputs/HtmlEditorViewer';
import { TextInput } from '../../../designSystem/inputs/TextInput';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { ProtectedPageWrapper } from '../../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { H2 } from '../../../designSystem/typography/Heading';
import { ArticleImportanceSelector } from '../_components/ArticleImportanceSelector';
import { Navigate } from 'react-router-dom';
import { Label } from '../../../designSystem/Label';

export function InternalArticleListPage (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [listing, setListing] = React.useState<InternalSubscriberArticleListing[]>([]);
    const [searchText, setSearchText] = React.useState('');
    const [filterText, setFilterText] = React.useState('');
    const [currentOwners, setCurrentOwners] = React.useState<PublicAmorgUser[]>([]);
    const [selectedOwnerId, setSelectedOwnerId] = React.useState('');
    const [showCreate, setShowCreate] = React.useState(false);
    const [sort, setSort] = React.useState<'title' | 'description' | 'version' | 'owner' | 'evaluationImportance'>('title');
    const [sortAscending, setSortAscending] = React.useState(true);

    const [redirect, setRedirect] = React.useState('');

    React.useEffect(() =>
    {
        document.title = 'Amorgy - Lovservice: Oversigt';
        const getData = async () =>
        {
            setLoading(true);
            const list = await getInternalArticles();
            setListing(list);

            const currentOwners = await getCurrentArticleOwners();
            setCurrentOwners(currentOwners);
            
            setLoading(false);
        };
        getData();
    }, []);

    async function handleCreatedInternalArticle(/*internalArticleId:string*/)
    {
        setShowCreate(false);

        setLoading(true);
        const list = await getInternalArticles();
        setListing(list);
        setLoading(false);
    }

    function applyFilter(): InternalSubscriberArticleListing[]
    {
        return listing.filter(a => filterText === '' || a.title.toLowerCase().includes(filterText.toLowerCase()) || a.description.toLowerCase().includes(filterText.toLowerCase()))
            .filter(a => !selectedOwnerId || a.owner?.userId === selectedOwnerId);
    }

    function applySort(data:InternalSubscriberArticleListing[])
    {
        function evaluationImportanceSortValue(importance: ArticleChangeImportance | null | undefined): number
        {
            switch (importance)
            {
                case 'high':
                    return 3;
                case 'medium':
                    return 2;
                case 'low':
                    return 1;
                default:
                    return 4;
            }
        }

        const sorted = [...data].sort((a, b) =>
        {

            let compareValue = 0;
            switch (sort)
            {
                case 'title':
                    compareValue = a.title.localeCompare(b.title);
                    break;
                case 'description':
                    compareValue = a.description.localeCompare(b.description);
                    break;
                case 'version':
                    compareValue = a.version - b.version;
                    break;
                case 'owner':
                    compareValue = (a.owner?.displayName || '').localeCompare(b.owner?.displayName || '');
                    break;
                case 'evaluationImportance':
                    compareValue = evaluationImportanceSortValue(a.evaluationImportance) - evaluationImportanceSortValue(b.evaluationImportance);
                    break;
            }
            return sortAscending ? compareValue : -compareValue;
        });
        return sorted;
    }


    let filteredSortedListing: InternalSubscriberArticleListing[] = [];
    if(listing)
    {
        filteredSortedListing = applyFilter();
        filteredSortedListing = applySort(filteredSortedListing);
    }

    return (

        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey={'articles'}>
            <SingleColumnLayout
                wide
                noDivider
                contentContainer={
                    <Flex flexDirection='column' gap={32}>

                        {redirect &&
                            <Navigate to={redirect} />
                        }

                        <LoadingPageOverlay loading={loading} />

                        <PageHeader 
                            headerStyle="secondary-gray"
                            headerIcon='gavel'
                            headerText='Lovservice'
                            buttons={
                                <ToolBarContainer>
                                    <Button variant="text" icon={'help'} toolTipText='Du kan bruge andre forpligtelser og krav til at dokumentere krav og forpligtelser, som ikke er lovkrav. Eksempelvis interne politikker og procedurer.' />
                                </ToolBarContainer>
                            }
                            breadCrumbs={[
                                { text: 'Tilbage til oversigten', url: '/articles', leadingIcon: 'arrow_back' },
                            ]}
                        />

                        <Flex justifyContent='space-between' alignItems='center'>
                            <Flex gap={8} alignItems='center'>
                                <Icon name="data_files_document" />
                                <H2>Andre forpligtelser</H2>
                            </Flex>
                            <Button variant='filled' text='Opret forpligtelse'
                                onClick={() => setShowCreate(true)} />
                        </Flex>

                        {!listing || listing.length === 0 &&
                            <MessageCard variant='info'>                                
                                <span>Der er endnu ikke oprettet andre forpligtelser og krav</span>
                            </MessageCard>
                        }

                        {listing && listing.length > 0 &&
                        <>
                            <Flex gap={16} alignItems='flex-end'>

                                <FormField
                                    label='Søgetekst'
                                    inputComponent={
                                        <Flex gap={4} alignItems='center'>
                                            <TextInput trailingIcon='interface_actions_search'
                                                value={searchText}
                                                onChange={(value) => setSearchText(value)}
                                                onKeyDown={(e) =>
                                                {
                                                    if (e.key === 'Enter')
                                                        setFilterText(searchText);
                                                }} />

                                            <Button variant='text' text='Søg' onClick={() => { 
                                                setFilterText(searchText);
                                            }} />

                                            <Button variant='text' icon='actions_close' text='Ryd filter' onClick={() => { 
                                                setSearchText('');
                                                setFilterText('');
                                            }} />
                                        </Flex>
                                    }
                                />
                            </Flex>

                            
                            {currentOwners.length > 0 &&
                                <FormField
                                    label='Filtrér efter ansvarlig'
                                    inputComponent={
                                        <Flex gap={8}>
                                            {currentOwners.map(owner => 
                                                <FullUserAvatar
                                                    key={owner.id}
                                                    user={{...owner}}
                                                    selected={selectedOwnerId === owner.id}
                                                    onClick={() => setSelectedOwnerId(selectedOwnerId === owner.id ? '' : owner.id)}
                                                />
                                            )}
                                        </Flex>
                                    }
                                />
                            }

                            <section>
                                <Table>
                                    <thead>
                                        <tr>
                                            <TableHeaderWithSort
                                                text={'Title'} 
                                                isActiveSort={sort === 'title'}
                                                ascending={sortAscending}
                                                onClick={() =>
                                                {
                                                    setSort('title');
                                                    setSortAscending(sort === 'title' ? !sortAscending : true);
                                                }} />
                                            <th>Beskrivelse</th>
                                            <TableHeaderIconWithSort 
                                                icon="stacks" 
                                                tooltipContent='Seneste version' 
                                                width='80px'
                                                isActiveSort={sort === 'version'}
                                                ascending={sortAscending}
                                                onClick={() =>
                                                {
                                                    setSort('version');
                                                    setSortAscending(sort === 'version' ? !sortAscending : true);
                                                }}
                                                 />
                                            <TableHeaderIconWithSort 
                                                icon="interface_user_user" 
                                                tooltipContent='Ansvarlig' 
                                                width='80px'
                                                isActiveSort={sort === 'owner'}
                                                ascending={sortAscending}
                                                onClick={() =>
                                                {
                                                    setSort('owner');
                                                    setSortAscending(sort === 'owner' ? !sortAscending : true);
                                                }}
                                                 />
                                            <TableHeaderIconWithSort 
                                                icon="thumbs_up_down" 
                                                tooltipContent='Vurderet betydning' 
                                                width='80px'
                                                isActiveSort={sort === 'evaluationImportance'}
                                                ascending={sortAscending}
                                                onClick={() =>
                                                {
                                                    setSort('evaluationImportance');
                                                    setSortAscending(sort === 'evaluationImportance' ? !sortAscending : true);
                                                }}
                                                 />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSortedListing.map(article => (
                                            <TableRowWithOnClick key={article.id} onClick={() => setRedirect(`/articles/internal/${article.id}`)}>
                                                <td>{article.title}</td>
                                                <td>
                                                    <HtmlEditorViewer content={article.description.replace(/<[^>]*>/g, '')}  />
                                                </td>
                                                <td>{article.version}</td>
                                                <td>
                                                    {article.owner
                                                        ?   <UserAvatar user={article.owner} size="24px" />
                                                        :   undefined
                                                    }
                                                </td>
                                                <td>
                                                    {article.evaluationImportance
                                                        ?   <ImportanceAvatar importance={article.evaluationImportance} 
                                                                mode="internal-evaluation" size="20px" />
                                                        :   undefined
                                                    }
                                                </td>
                                            </TableRowWithOnClick>
                                        ))}
                                    </tbody>
                                </Table>
                            </section>
                        </>
                        }

                        <CreateInternalArticleModal
                            show={showCreate}
                            onCreated={handleCreatedInternalArticle}
                            onClose={() => setShowCreate(false)}
                        />

                    </Flex>

                } />

        </ProtectedPageWrapper>
    );
}

type CreateInternalArticleModalProps = {
    show:boolean,
    onCreated:(internalArticleId:string)=> void,
    onClose: () => void
}
function CreateInternalArticleModal(props:CreateInternalArticleModalProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [state, setState] = React.useState<
    {
        title:string,
        description:string,
        ownerUserId:string,
        evaluationImportance:ArticleChangeImportance | null,
        evaluationText:string,
    }>(
        {
            title:'',
            description:'',
            ownerUserId:'',
            evaluationImportance:null,
            evaluationText:''
        }
    );
    
    const [errors, setErrors] = React.useState<{title:string, description:string, evaluationText:string, submission:string}>(
        {
            title:'',
            description:'',
            evaluationText:'',
            submission:''
        }
    );

    React.useEffect(() => {
        setState({
            title:'',
            description:'',
            ownerUserId:'',
            evaluationImportance:null,
            evaluationText:''
        })
        setErrors({
            title:'',
            description:'',
            evaluationText:'',
            submission:''
        });
    }, [props.show]);

    async function handleClickCreate()
    {
        const validationErrors = {
            title: fieldValidation.internalArticle.title(state.title) ? '' : 'Skal udfyldes. Maks. 400 tegn',
            description: fieldValidation.internalArticle.description(state.description) ? '' : 'Skal udfyldes. Maks. 5000 tegn',
            evaluationText: fieldValidation.internalArticleChange.evaluationText(state.evaluationText) ? '' : 'Maks. 5000 tegn',
            submission: ''
        };
        setErrors(validationErrors);

        if(!!validationErrors.title || !!validationErrors.description || !!validationErrors.evaluationText)
        {
            return;
        }
        else
        {
            setLoading(true);

            const newInternalArticleId = rng.getRandomId();
            const result = await createInternalArticle(
                newInternalArticleId,
                state.title,
                state.description,
                state.ownerUserId || null,
                rng.getRandomId(),
                '',
                new Date(),
                state.evaluationImportance,
                state.evaluationText
            );
            
            setLoading(false);

            if(result.isSuccess)
                props.onCreated(newInternalArticleId);
            else
                setErrors({...validationErrors, submission: 'Der opstod en fejl under oprettelsen af forpligtelsen. Prøv igen senere eller kontakt support'});
        }
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <Modal
                showModal={props.show}
                onCancel={props.onClose}
                title='Opret anden forpligtelse'
                content={
                    <Flex flexDirection='column' gap={16}>

                        {!!errors.submission &&
                            <MessageCard variant='error'>
                                <span>{errors.submission}</span>
                            </MessageCard>
                        }

                        <TextField
                            label='Titel'
                            value={state.title}
                            errorMessage={errors.title}
                            onChange={(value) => setState({...state, title:value})} 
                        />

                        <FormField
                            label='Beskrivelse'
                            inputComponent={
                                <HtmlEditor
                                    id={'create-internal-article-description'}
                                    value={state.description}
                                    onChange={(value) => setState({...state, description:value})}
                                />
                            }
                            message={errors.description}
                            inErrorState={!!errors.description}
                        />

                        <FormField
                            label='Ansvarlig'
                            inputComponent={
                                <UserSelectorAvatarButton
                                    noSelectionText='Ingen valgt'
                                    selectedUserId={state.ownerUserId || ''}
                                    onUserChange={(user) => setState({...state, ownerUserId:user?.id || ''})}
                                />
                            }
                        />

                        <Flex justifyContent='space-between' alignItems='center'>
                            <Label>Evaluering</Label>
                            <ArticleImportanceSelector
                                mode='internal-evaluation'
                                selectedImportance={state.evaluationImportance}
                                onChangeImportance={(importance) => setState({...state, evaluationImportance:importance})}
                            />
                        </Flex>

                        <FormField
                            label='Evaluering'
                            inputComponent={
                                <HtmlEditor
                                    id={'create-internal-article-evaluation-text'}
                                    value={state.evaluationText}
                                    onChange={(value) => setState({...state, evaluationText:value})}
                                />
                            }
                            message={errors.evaluationText}
                            inErrorState={!!errors.evaluationText}
                        />



                        <Button variant='filled' text='Opret forpligtelse' onClick={handleClickCreate} />
                    </Flex>
                }
            />
        </>
        
    );
}